/**
 * TextArea Component
 * Reusable textarea component with various states and styles
 */

import React, { forwardRef } from 'react';
import styles from './TextArea.module.css';

export interface TextAreaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
    label?: string;
    error?: string;
    helperText?: string;
    fullWidth?: boolean;
    resize?: 'none' | 'vertical' | 'horizontal' | 'both';
    autoGrow?: boolean;
}

const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(
    ({
        label,
        error,
        helperText,
        fullWidth = false,
        resize = 'vertical',
        className = '',
        autoGrow = false,
        value,
        placeholder,
        style,
        ...props
    }, ref) => {
        const textareaClasses = [
            styles.textarea,
            error && styles.textarea__error,
            fullWidth && styles.textarea__fullWidth,
            className
        ].filter(Boolean).join(' ');
        
        const currentValue = value ?? props.defaultValue ?? '';
        const replicated = autoGrow
            ? `${(typeof currentValue === 'number' ? String(currentValue) : (currentValue as string)) || (placeholder ?? '')}\n`
            : undefined;

        return (
            <div className={`${styles.container} ${fullWidth ? styles.container__fullWidth : ''}`}>
                {label && <label className={styles.label}>{label}</label>}

                <div
                    className={`${styles.field} ${autoGrow ? styles.autogrow : ''}`}
                    data-replicated-value={replicated}
                >
                    <textarea
                        ref={ref}
                        className={textareaClasses}
                        style={{ resize, ...style }}
                        value={value}
                        placeholder={placeholder}
                        {...props}
                    />
                </div>

                {(error || helperText) && (
                    <div className={`${styles.message} ${error ? styles.message__error : ''}`}>
                        {error || helperText}
                    </div>
                )}
            </div>
        );
    }
);

TextArea.displayName = 'TextArea';
export default TextArea;
